import React from "react";
import { motion } from "framer-motion";
import { Globe, DollarSign, Lightbulb, Cpu, TrendingUp } from "lucide-react";
import PageLayout from "@/components/PageLayout";

const BENEFITS = [
  { icon: Globe, title: "Remote-First", desc: "Work from anywhere in the world. Our team spans multiple time zones and we embrace asynchronous collaboration." },
  { icon: DollarSign, title: "Competitive Compensation", desc: "Salary packages benchmarked to top-tier tech companies, with equity participation for early team members." },
  { icon: Lightbulb, title: "Research Freedom", desc: "Dedicated time for exploration and publication. We encourage conference attendance and open-source contributions." },
  { icon: Cpu, title: "Cutting-Edge Stack", desc: "Work with quantum computing APIs, state-of-the-art LLMs, and infrastructure that processes millions of AI requests daily." },
  { icon: TrendingUp, title: "Growth & Impact", desc: "Join at the ground floor of a platform poised to transform the AI industry. Your work will directly shape the product." },
];

const ROLES = [
  { title: "Senior Backend Engineer (Python/Node.js)", location: "Remote (EU preferred)", type: "Full-time" },
  { title: "Quantum Computing Researcher", location: "Remote / H\u00f8ylandsbygd, Norway", type: "Full-time" },
  { title: "Full-Stack Engineer (React/TypeScript)", location: "Remote", type: "Full-time" },
  { title: "AI/ML Engineer \u2014 Multi-Agent Systems", location: "Remote", type: "Full-time" },
  { title: "Developer Relations Engineer", location: "Remote (US/EU)", type: "Full-time" },
  { title: "Head of Sales \u2014 Enterprise AI", location: "Remote (US)", type: "Full-time" },
];

export default function CareersPage() {
  return (
    <PageLayout>
      <div className="max-w-5xl mx-auto px-6 py-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4" data-testid="careers-heading">Build the Future of AI With Us</h1>
          <p className="text-lg text-[var(--qn-foreground-muted)] max-w-3xl mb-16 leading-relaxed">
            Join a team of researchers, engineers, and visionaries working at the frontier of quantum computing and agentic AI. We are building technology that will define the next decade of artificial intelligence.
          </p>
        </motion.div>

        <section className="mb-16">
          <h2 className="text-lg font-semibold mb-4 text-[var(--qn-primary)]">Why QNNEAL</h2>
          <p className="text-[var(--qn-foreground-muted)] leading-relaxed">
            At QNNEAL, you will work on problems that matter &mdash; from quantum optimization algorithms to large-scale distributed AI systems. We are a small, focused team where every contribution has outsized impact. We value intellectual curiosity, technical excellence, and the courage to challenge conventional approaches.
          </p>
        </section>

        <section className="mb-16">
          <h2 className="text-lg font-semibold mb-6 text-[var(--qn-primary)]">What We Offer</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {BENEFITS.map((b, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 * i }} className="p-5 rounded-xl border border-[var(--qn-border)] bg-[var(--qn-surface)]">
                <b.icon className="w-7 h-7 text-[var(--qn-primary)] mb-3" />
                <h3 className="font-semibold text-sm mb-1">{b.title}</h3>
                <p className="text-sm text-[var(--qn-foreground-muted)] leading-relaxed">{b.desc}</p>
              </motion.div>
            ))}
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-lg font-semibold mb-2 text-[var(--qn-primary)]">Open Positions</h2>
          <p className="text-sm text-[var(--qn-foreground-muted)] mb-6">If you do not see a perfect match but believe you can contribute, we welcome speculative applications.</p>
          <div className="space-y-3">
            {ROLES.map((r, i) => (
              <motion.div key={i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.06 * i }} className="flex flex-col sm:flex-row sm:items-center justify-between p-4 rounded-lg border border-[var(--qn-border)] bg-[var(--qn-surface)]" data-testid={`career-role-${i}`}>
                <div>
                  <h3 className="font-semibold text-sm">{r.title}</h3>
                  <p className="text-xs text-[var(--qn-foreground-muted)]">{r.location}</p>
                </div>
                <span className="text-xs font-medium text-[var(--qn-primary)] mt-2 sm:mt-0">{r.type}</span>
              </motion.div>
            ))}
          </div>
        </section>

        <section className="p-6 rounded-xl border border-[var(--qn-primary)]/20 bg-[var(--qn-primary-muted)]">
          <h3 className="font-semibold mb-2">Interested?</h3>
          <p className="text-sm text-[var(--qn-foreground-muted)]">
            Send your CV and a brief note about why you are excited about QNNEAL to{" "}
            <a href="mailto:careers@qnneal.dev" className="text-[var(--qn-primary)] hover:underline" data-testid="careers-email-link">careers@qnneal.dev</a>.
            We review every application personally.
          </p>
        </section>
      </div>
    </PageLayout>
  );
}
